import { P0_FLOWS, P1_FLOWS } from './constant';
import {
  isPathLockedModule,
  moduleFromPathname,
  routeMatches,
} from './moduleMapping';
import { owningTeamForModule } from './ownership';
import {
  DEFAULT_INCIDENT_PRIORITY,
  priorityFromFlowTier,
} from './priority';
import type {
  ResolvedSentryContext,
  SentryFlowDefinition,
  SentryFlowTier,
} from './types';

export const normalizeEventSuffix = (
  analyticsEvent?: string | null,
): string => {
  if (!analyticsEvent) return '';
  const trimmed = analyticsEvent.trim();
  const gt = trimmed.lastIndexOf('>');
  return gt >= 0 ? trimmed.slice(gt + 1).trim() : trimmed;
};

export const parsePgEvent = (
  analyticsEvent?: string | null,
): { route: string; action: string } => {
  const trimmed = (analyticsEvent ?? '').trim();
  const gt = trimmed.lastIndexOf('>');
  if (gt < 0) return { route: '', action: trimmed };
  return {
    route: trimmed.slice(0, gt).replace(/^PG:/, ''),
    action: trimmed.slice(gt + 1).trim(),
  };
};

const eventMatchesFlow = (
  flowDef: SentryFlowDefinition,
  suffix: string,
): boolean => {
  if (flowDef.eventPattern?.test(suffix)) return true;
  const events = Array.isArray(flowDef.event) ? flowDef.event : [flowDef.event];
  if (!events.length) return false;
  return events.some(e => e === suffix);
};

const findFlow = (
  flows: readonly SentryFlowDefinition[],
  analyticsEvent?: string | null,
  pathname?: string | null,
): SentryFlowDefinition | undefined => {
  const { route, action } = parsePgEvent(analyticsEvent);
  const suffix = action || normalizeEventSuffix(analyticsEvent);
  if (!suffix) return undefined;

  const routeCtx = route || pathname || '';
  return flows.find(
    f =>
      eventMatchesFlow(f, suffix) &&
      routeMatches(routeCtx, f.routeHints) &&
      (!pathname || routeMatches(pathname, f.routeHints)),
  );
};

export const findP0Flow = (
  analyticsEvent?: string | null,
  pathname?: string | null,
): SentryFlowDefinition | undefined =>
  findFlow(P0_FLOWS, analyticsEvent, pathname);

export const findP1Flow = (
  analyticsEvent?: string | null,
  pathname?: string | null,
): SentryFlowDefinition | undefined =>
  findFlow(P1_FLOWS, analyticsEvent, pathname);

const contextFromFlow = (
  match: SentryFlowDefinition,
  tier: SentryFlowTier,
  analyticsEvent?: string | null,
): ResolvedSentryContext => ({
  module: match.module,
  feature: match.feature,
  incident_priority: priorityFromFlowTier(tier),
  owning_team: owningTeamForModule(match.module),
  flow_tier: tier,
  matched_event: normalizeEventSuffix(analyticsEvent),
});

/** Combines flow match, module fallback, and ownership into one object */
export const resolveSentryContext = (
  analyticsEvent?: string | null,
  pathname?: string | null,
): ResolvedSentryContext => {
  const pathModule = moduleFromPathname(pathname);

  const p0 = findP0Flow(analyticsEvent, pathname);
  if (p0 && (!isPathLockedModule(pathModule) || p0.module === pathModule)) {
    return contextFromFlow(p0, 'p0', analyticsEvent);
  }

  const p1 = findP1Flow(analyticsEvent, pathname);
  if (p1 && (!isPathLockedModule(pathModule) || p1.module === pathModule)) {
    return contextFromFlow(p1, 'p1', analyticsEvent);
  }

  if (pathModule) {
    return {
      module: pathModule,
      incident_priority: DEFAULT_INCIDENT_PRIORITY,
      owning_team: owningTeamForModule(pathModule),
    };
  }

  return { incident_priority: DEFAULT_INCIDENT_PRIORITY };
};

/** @deprecated Use resolveSentryContext */
export const resolveFlowMeta = resolveSentryContext;
